import {
  Align,
  Arrange,
  Button,
  Container,
  Icon,
  Loader,
  Spacer,
  SvgClock,
  SvgInfo,
  Text,
  TextButton,
} from '@loomhq/lens';
import produce from 'immer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import { updateShareVideoInTrimMode } from 'actions/shareVideo';
import {
  setSelectedPlayRangeIdx,
  updateShowTrimExitWarning,
} from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  setCurrentCaption,
  updateVideoPlayerEnded,
  updateVideoPlayerNotEnded,
} from 'actions/videoPlayer';
import {
  TRIM_CANCELLED,
  TRIM_CHANGE_MADE,
  TRIM_DELETE,
  TRIM_HELP_ARTICLE_CLICKED,
  TRIM_REVERT_TO_ORIGINAL,
} from 'constants/events';
import {
  deletePlayRangeCreator,
  leaveTrimModeCreator,
  patchTrimVideoCreator,
  redoPlayRangeChangeCreator,
  splitPlayRangeCreator,
  syncPlayRangesFromTrimRangesCreator,
  undoPlayRangeChangeCreator,
  updatePlayRangesCreator,
} from 'creators/trimming';
import { updateVideoPlayerDurationCreator } from 'creators/videoPlayer';
import {
  selectCanDeletePlayRange,
  selectCanRedoPlayRange,
  selectCanSplitPlayRange,
  selectCanUndoPlayRange,
  selectPlayRanges,
  selectSelectedPlayRangeIdx,
  selectTrimNewChanges,
} from 'selectors/trimming';
import {
  selectVideoHasAudioEnabled,
  selectVideoId,
  selectVideoWaveformStatus,
  selectVideoWaveformUrl,
} from 'selectors/video';
import {
  selectPlayerTime,
  selectRequestedPlayerTime,
  selectVideo,
  selectVideoDuration,
  selectVideoPlayerIsFetching,
  selectVideoPlayerReadyToPlayOnSharePage,
} from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { secondsToVideoTS } from 'utilities/time';
import {
  findCurrentPlayRangeIdx,
  getTrimVideoDuration,
  makePlayRangeId,
  makeTrimRangesFromPlayRanges,
} from 'utilities/trimming';

import ControlBar from './control-bar';
import Waveform from './waveform';

import styles from './styles.module.less';

const isTimeInPlayRanges = (secs, playRanges) =>
  findCurrentPlayRangeIdx(secs, playRanges) !== -1;

const findClosestPlayRangeAndLoc = (time, playRanges) => {
  let playRangeIdx: number;
  let closestLoc: number;
  let distance = Infinity;

  for (let idx = 0; idx < playRanges.length; idx++) {
    const range = playRanges[idx];

    if (Math.abs(range.from - time) < distance) {
      closestLoc = range.from;
      playRangeIdx = idx;
      distance = Math.abs(range.from - time);
    }

    if (Math.abs(range.to - time) < distance) {
      closestLoc = range.to;
      playRangeIdx = idx;
      distance = Math.abs(range.to - time);
    }
  }

  // @ts-expect-error ignore due to enabling strict null checks
  return { closestLoc, playRangeIdx };
};

const EditTools = (): JSX.Element => {
  const [waveformData, setWaveformData] = useState([]);

  const dispatch = useDispatch();
  const currentTime = useSelector(selectPlayerTime);
  const requestedCurrentTime = useSelector(selectRequestedPlayerTime);
  const duration = useSelector(selectVideoDuration);
  const waveformUrl = useSelector(selectVideoWaveformUrl);
  const waveformGenerationStatus = useSelector(selectVideoWaveformStatus);
  const newTrimChanges = useSelector(selectTrimNewChanges);
  const canUndo: boolean = useSelector(selectCanUndoPlayRange);
  const canRedo: boolean = useSelector(selectCanRedoPlayRange);
  const video = useSelector(selectVideo);
  const videoHasAudio = useSelector(selectVideoHasAudioEnabled);
  const canSplitPlayRange = useSelector(selectCanSplitPlayRange);
  const canDeletePlayRange = useSelector(selectCanDeletePlayRange);
  const selectedPlayRangeIdx = useSelector(selectSelectedPlayRangeIdx);

  const playRanges = useSelector(selectPlayRanges);
  const videoId = useSelector(selectVideoId);
  const trimRanges = makeTrimRangesFromPlayRanges(playRanges, duration);

  const isFetching = useSelector(selectVideoPlayerIsFetching);
  const readyToPlay = useSelector(selectVideoPlayerReadyToPlayOnSharePage);

  const trimButtonsRef = useCallback(buttonsNode => {
    if (buttonsNode) {
      buttonsNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, []);

  // This effect is here b/c we do not have an accurate duration when the
  // video first loads. The duration in video_player is the duration of the
  // trimmed video and the duration in video_player.video.video_properties
  // is rounded which can mess up our play range values.
  useEffect(() => {
    dispatch(syncPlayRangesFromTrimRangesCreator());
  }, [dispatch, duration]);

  useEffect(() => {
    const getWaveformJSON = async () => {
      if (!waveformUrl) {
        return;
      }

      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      const resp = await fetch(waveformUrl);

      if (!resp.ok) {
        throw new Error('Failed to fetch waveform json');
      }

      const data = await resp.json();

      setWaveformData(data);
    };

    if (waveformUrl) {
      getWaveformJSON();
    }
  }, [waveformUrl]);

  // Handle URL changes, back button, page reload with unsaved changes
  const unloadEventListenerRef = useRef(() => '');

  useEffect(() => {
    const removeEventListener = () => {
      if (unloadEventListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          unloadEventListenerRef.current,
          { capture: true }
        );
      }
    };

    const showWarningModal = event => {
      event.preventDefault();

      return (event.returnValue =
        'There are unsaved changes to your video. Are you sure you want to discard them?');
    };

    removeEventListener();

    if (newTrimChanges) {
      // @ts-expect-error ignore due to enabling strict null checks
      unloadEventListenerRef.current = showWarningModal;
      window.addEventListener('beforeunload', unloadEventListenerRef.current, {
        capture: true,
      });
    }

    return removeEventListener;
  }, [newTrimChanges, dispatch]);

  const updateSelectedPlayRange = () => {
    if (requestedCurrentTime != null) {
      return;
    }

    if (
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      !playRanges[selectedPlayRangeIdx] ||
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      currentTime < playRanges[selectedPlayRangeIdx].from ||
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      currentTime > playRanges[selectedPlayRangeIdx].to
    ) {
      const idx = findCurrentPlayRangeIdx(currentTime, playRanges);

      if (idx !== -1) {
        dispatch(setSelectedPlayRangeIdx(idx));
      }
    }
  };

  /* keep the playhead inside the play ranges */
  const placePlayheadInPlayRanges = () => {
    // If we are in a play range already or time is being updated
    if (selectedPlayRangeIdx !== -1 || requestedCurrentTime != null) {
      return;
    }

    // If we are not in a play range, move to the closest one
    const { closestLoc, playRangeIdx } = findClosestPlayRangeAndLoc(
      currentTime,
      playRanges
    );

    dispatch(setSelectedPlayRangeIdx(playRangeIdx));
    dispatch(requestVideoPlayerCurrentTime(closestLoc));
  };

  useEffect(placePlayheadInPlayRanges, [
    dispatch,
    playRanges,
    currentTime,
    selectedPlayRangeIdx,
    requestedCurrentTime,
  ]);

  useEffect(updateSelectedPlayRange, [
    currentTime,
    playRanges,
    selectedPlayRangeIdx,
    requestedCurrentTime,
    dispatch,
  ]);

  useEffect(() => {
    // If we are at the end of the last play range
    // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
    if (currentTime === playRanges[playRanges.length - 1]?.to) {
      dispatch(updateVideoPlayerEnded());
    } else {
      dispatch(updateVideoPlayerNotEnded());
    }
  }, [currentTime, playRanges, dispatch]);

  const updatePlayRange = ({ from, to, id }) => {
    const newPlayRanges = produce(playRanges, draftRanges => {
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      const range = draftRanges.find(range => range.id === id);

      if (range) {
        range.from = from;
        range.to = to;
      }
    });

    dispatch(updatePlayRangesCreator(newPlayRanges));
  };

  const deletePlayRange = () => {
    dispatch(deletePlayRangeCreator());

    analytics.track(TRIM_DELETE, {
      id: videoId,
      has_been_trimmed: true,
      event_type: 'click',
    });
  };

  const splitPlayRange = () => {
    dispatch(splitPlayRangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'split',
      hotkey: false,
      has_been_trimmed: trimRanges?.length > 0,
      event_type: 'click',
    });
  };

  const handleClick = (event: React.MouseEvent, { secs }: { secs: number }) => {
    if (isTimeInPlayRanges(secs, playRanges)) {
      dispatch(requestVideoPlayerCurrentTime(secs));
    }
  };

  const saveChanges = () => {
    dispatch(
      patchTrimVideoCreator(video, () => {
        dispatch(updateShareVideoInTrimMode(false));
        dispatch(updateVideoPlayerDurationCreator(trimVideoDuration));
        dispatch(showConfirmationModal('Your changes have been saved.'));
      })
    );
    dispatch(setCurrentCaption(null));
  };

  const exitWithoutSaving = () => {
    if (newTrimChanges) {
      dispatch(updateShowTrimExitWarning(true));
    } else {
      goBackToVideo();
    }
  };

  const goBackToVideo = () => {
    dispatch(leaveTrimModeCreator());

    analytics.track(TRIM_CANCELLED, {
      id: videoId,
      has_been_trimmed: trimRanges?.length > 0,
      event_type: 'click',
    });
  };

  const undo = () => {
    dispatch(undoPlayRangeChangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'undo',
      hotkey: false,
      has_been_trimmed: trimRanges?.length > 0,
      event_type: 'click',
    });
  };

  const redo = () => {
    dispatch(redoPlayRangeChangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'redo',
      hotkey: false,
      has_been_trimmed: trimRanges?.length > 0,
      event_type: 'click',
    });
  };

  useHotkeys(
    'cmd+z,ctrl+z',
    e => {
      e.preventDefault();
      undo();
    },
    { enabled: canUndo }
  );
  useHotkeys(
    'cmd+shift+z,ctrl+shift+z',
    e => {
      e.preventDefault();
      redo();
    },
    {
      enabled: canRedo,
    }
  );

  const revertToOriginal = () => {
    dispatch(
      updatePlayRangesCreator([
        { id: makePlayRangeId(), from: 0, to: duration },
      ])
    );

    analytics.track(TRIM_REVERT_TO_ORIGINAL, {
      id: videoId,
      has_been_trimmed: trimRanges?.length > 0,
      event_type: 'click',
    });
  };

  const canRevertToOriginal =
    // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
    playRanges.length > 0 &&
    // @ts-expect-error ignore due to enabling strict null checks
    (playRanges[0]?.from != 0 || playRanges[0]?.to != duration);

  const controlBarProps = {
    currentTime,
    canSplitPlayRange,
    splitPlayRange,
    canDeletePlayRange,
    deletePlayRange,
    canUndo,
    canRedo,
    undo,
    redo,
    revertToOriginal,
    canRevertToOriginal,
  };

  const isReady =
    // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
    !isFetching && playRanges && playRanges.length > 0 && readyToPlay;

  if (!isReady) {
    return (
      <Container paddingTop="xlarge">
        <Align alignment="center">
          <Loader size="large" />
        </Align>
      </Container>
    );
  }

  const trimVideoDuration = getTrimVideoDuration(duration, trimRanges);
  const durationChanged = duration !== trimVideoDuration;

  return (
    <Arrange justifyContent="stretch" autoFlow="row">
      <Spacer top="large" />
      {/* @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341 */}
      <ControlBar {...controlBarProps} />
      <Spacer top="small" />
      <Waveform
        viewboxHeight={80}
        peaks={waveformData}
        peakCount={1000}
        cursorWidth={2}
        cursorVerticalMargin={16}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        cursorPos={currentTime}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        duration={duration}
        normalize={true}
        sectionOutlineWidth={2}
        onClick={handleClick}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        playRanges={playRanges}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        selectedPlayRange={selectedPlayRangeIdx}
        updatePlayRange={updatePlayRange}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        waveformStatus={waveformGenerationStatus}
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        hasAudio={videoHasAudio}
      />
      <div ref={trimButtonsRef}>
        <Align alignment="center">
          <div className={styles.durationChange}>
            {durationChanged ? (
              <Arrange gap="xsmall">
                <Icon color="bodyDimmed" icon={<SvgClock />} />{' '}
                <Text
                  size="medium"
                  color="bodyDimmed"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {
                    // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
                    secondsToVideoTS(duration, 1)
                  }
                </Text>{' '}
                <Text size="medium" color="bodyDimmed" fontWeight="medium">
                  &rarr;
                </Text>
                <Text
                  size="medium"
                  color="body"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(trimVideoDuration, 1)}
                </Text>
              </Arrange>
            ) : (
              <Text size="medium">
                Trim your video by dragging the handles and using the Split
                button.
              </Text>
            )}
          </div>

          <TextButton
            size="small"
            icon={<Icon size={2} icon={<SvgInfo />} color="primary" />}
            htmlTag="a"
            // @ts-expect-error removed lens.d.ts
            target="_blank"
            href="https://support.loom.com/hc/en-us/articles/360002217478-How-to-trim-your-Loom-video-"
            onClick={() =>
              analytics.track(TRIM_HELP_ARTICLE_CLICKED, {
                id: videoId,
                has_been_trimmed: trimRanges?.length > 0,
                event_type: 'click',
              })
            }
          >
            <Text color="primary" size="small" fontWeight="medium">
              View help article
            </Text>
          </TextButton>

          <Spacer top="medium" />
          <Arrange gap="small">
            <Button onClick={exitWithoutSaving}>Cancel</Button>
            <Button
              variant="primary"
              isDisabled={!newTrimChanges}
              onClick={saveChanges}
            >
              Save changes
            </Button>
          </Arrange>
        </Align>
      </div>
    </Arrange>
  );
};

export default EditTools;
